import React, { useState, useEffect, useCallback } from 'react';
import { ApiServiceAdmin } from '../services/ApiServiceAdmin';

interface Props {
  isRTL: boolean;
  currentLanguage: string;
  canEdit: boolean;
}

type LocaleKey = 'ar' | 'fr' | 'en';
type LabelsForm = Record<LocaleKey, { name: string; description: string }>;

interface SubjectRow {
  id: number;
  name: string;
  description?: string;
  status: string;
  labels?: LabelsForm;
}

const EMPTY_LABELS: LabelsForm = {
  ar: { name: '', description: '' },
  fr: { name: '', description: '' },
  en: { name: '', description: '' },
};

const api = ApiServiceAdmin.getInstance();

const LANG_LABELS: Record<LocaleKey, string> = {
  ar: 'العربية',
  fr: 'Français',
  en: 'English',
};

function labelsFromSubject(s: SubjectRow): LabelsForm {
  const L = s.labels || ({} as Partial<LabelsForm>);
  const pick = (loc: LocaleKey) => {
    const block = L[loc];
    const name = block?.name != null ? String(block.name).trim() : '';
    let description = block?.description != null ? String(block.description).trim() : '';
    if (description === name) description = '';
    return { name, description };
  };
  return { ar: pick('ar'), fr: pick('fr'), en: pick('en') };
}

function isActiveStatus(status: string) {
  return status === 'نشط' || status === 'active';
}

export const SubjectsSettingsTab: React.FC<Props> = ({ isRTL, currentLanguage, canEdit }) => {
  const [list, setList] = useState<SubjectRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit' | 'view'>('add');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<LabelsForm>(EMPTY_LABELS);
  const [formStatus, setFormStatus] = useState('نشط');
  const [modalError, setModalError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const locale = currentLanguage === 'fr' || currentLanguage === 'en' ? currentLanguage : 'ar';

  const txt = (fr: string, ar: string, en: string) =>
    isRTL ? ar : currentLanguage === 'fr' ? fr : en;

  const load = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const res = await api.getSubjects(locale);
      if (res?.success && Array.isArray(res.data)) {
        setList(res.data);
      } else {
        setList([]);
        if (res?.error) setListError(res.error);
      }
    } catch (e) {
      setListError(e instanceof Error ? e.message : String(e));
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [locale]);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setModalMode('add');
    setEditingId(null);
    setForm(EMPTY_LABELS);
    setFormStatus('نشط');
    setModalError(null);
    setModalOpen(true);
  };

  const openView = (s: SubjectRow) => {
    setModalMode('view');
    setEditingId(s.id);
    setForm(labelsFromSubject(s));
    setFormStatus(s.status || 'نشط');
    setModalError(null);
    setModalOpen(true);
  };

  const openEdit = (s: SubjectRow) => {
    setModalMode('edit');
    setEditingId(s.id);
    setForm(labelsFromSubject(s));
    setFormStatus(s.status || 'نشط');
    setModalError(null);
    setModalOpen(true);
  };

  const switchToEdit = () => {
    if (!canEdit || editingId == null) return;
    setModalMode('edit');
    setModalError(null);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setModalError(null);
  };

  const setField = (loc: LocaleKey, field: 'name' | 'description', value: string) => {
    setForm((p) => ({ ...p, [loc]: { ...p[loc], [field]: value } }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit || modalMode === 'view') return;
    setModalError(null);
    setSaving(true);
    try {
      const res =
        modalMode === 'add'
          ? await api.createSubject(form, formStatus)
          : await api.updateSubject(editingId!, form, formStatus);
      if (!res?.success) {
        setModalError(res?.error || txt('Erreur', 'خطأ', 'Error'));
        return;
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      setModalError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const card: React.CSSProperties = {
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    background: '#fff',
    overflow: 'hidden',
  };

  const readOnly = modalMode === 'view';
  const fieldStyle = (loc: LocaleKey): React.CSSProperties => ({
    width: '100%',
    padding: '8px 10px',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
    boxSizing: 'border-box',
    fontSize: '13px',
    background: readOnly ? '#f9fafb' : '#fff',
    color: readOnly ? '#374151' : undefined,
    cursor: readOnly ? 'default' : undefined,
  });

  const displayDescription = (loc: LocaleKey) => {
    const d = form[loc].description.trim();
    const n = form[loc].name.trim();
    if (d) return d;
    if (n) return n;
    return '—';
  };

  const statusBadge = (status: string): React.CSSProperties => {
    const active = isActiveStatus(status);
    return {
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: '999px',
      fontSize: '11px',
      fontWeight: 600,
      background: active ? '#ecfdf5' : '#f3f4f6',
      color: active ? '#047857' : '#6b7280',
    };
  };

  return (
    <div style={{ padding: '4px 0' }}>
      <div style={card}>
        {/* En-tête */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            flexWrap: 'wrap',
            padding: '14px 18px',
            borderBottom: '1px solid #f3f4f6',
            background: '#fafafa',
          }}
        >
          <div>
            <div style={{ fontSize: '15px', fontWeight: 700, color: '#1f2937' }}>
              {txt('Matières', 'المواد الدراسية', 'Subjects')}
              {!loading && (
                <span style={{ marginInlineStart: '8px', fontSize: '12px', fontWeight: 500, color: '#9ca3af' }}>
                  ({list.length})
                </span>
              )}
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', maxWidth: '520px', lineHeight: 1.4 }}>
              {txt(
                'Noms en arabe, français et anglais. La description vide reprend le nom à la création.',
                'الاسم مطلوب بثلاث لغات. الوصف الفارغ يُنسخ من الاسم عند الإضافة فقط.',
                'Names in ar, fr and en. Empty description uses the name on create only.'
              )}
            </div>
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={openAdd}
              style={{
                backgroundColor: '#059669',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '9px 18px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              + {txt('Ajouter', 'إضافة مادة', 'Add')}
            </button>
          )}
        </div>

        {listError && (
          <div style={{ padding: '12px 18px', color: '#b91c1c', fontSize: '13px', borderBottom: '1px solid #fecaca', background: '#fef2f2' }}>
            {listError}
          </div>
        )}

        {/* Liste */}
        {loading ? (
          <div style={{ padding: '28px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
            {txt('Chargement…', 'جاري التحميل…', 'Loading…')}
          </div>
        ) : list.length === 0 ? (
          <div style={{ padding: '28px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
            {txt('Aucune matière enregistrée.', 'لا توجد مواد مسجّلة.', 'No subjects yet.')}
            {canEdit && (
              <div style={{ marginTop: '12px' }}>
                <button
                  type="button"
                  onClick={openAdd}
                  style={{
                    background: 'none',
                    border: '1px dashed #059669',
                    color: '#059669',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '13px',
                  }}
                >
                  + {txt('Ajouter la première', 'إضافة أول مادة', 'Add the first one')}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '10px 18px', textAlign: isRTL ? 'right' : 'left', fontWeight: 600, color: '#6b7280', width: '48px' }}>#</th>
                  <th style={{ padding: '10px 18px', textAlign: isRTL ? 'right' : 'left', fontWeight: 600, color: '#6b7280', minWidth: '120px' }}>
                    {txt('Matière', 'المادة', 'Subject')}
                  </th>
                  <th style={{ padding: '10px 18px', textAlign: isRTL ? 'right' : 'left', fontWeight: 600, color: '#6b7280' }}>
                    {txt('Description', 'الوصف', 'Description')}
                  </th>
                  <th style={{ padding: '10px 18px', textAlign: 'center', fontWeight: 600, color: '#6b7280', width: '100px' }}>
                    {txt('Statut', 'الحالة', 'Status')}
                  </th>
                  {canEdit && (
                    <th style={{ padding: '10px 18px', textAlign: 'center', fontWeight: 600, color: '#6b7280', width: '90px' }}>
                      {txt('Actions', 'إجراءات', 'Actions')}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {list.map((s, idx) => {
                  const active = isActiveStatus(s.status);
                  const descText =
                    s.description && s.description !== s.name ? s.description : null;
                  return (
                    <tr
                      key={s.id}
                      onClick={() => openView(s)}
                      style={{
                        borderBottom: '1px solid #f3f4f6',
                        cursor: 'pointer',
                        transition: 'background 0.15s',
                        background: active ? 'transparent' : '#fafafa',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = active ? '#f9fafb' : '#f3f4f6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = active ? 'transparent' : '#fafafa';
                      }}
                    >
                      <td style={{ padding: '12px 18px', color: '#9ca3af' }}>{idx + 1}</td>
                      <td style={{ padding: '12px 18px' }}>
                        <div style={{ fontWeight: 600, color: active ? '#1f2937' : '#6b7280' }}>{s.name}</div>
                      </td>
                      <td
                        style={{
                          padding: '12px 18px',
                          color: '#6b7280',
                          fontSize: '12px',
                          lineHeight: 1.45,
                          maxWidth: '360px',
                        }}
                      >
                        {descText ?? <span style={{ color: '#d1d5db' }}>—</span>}
                      </td>
                      <td style={{ padding: '12px 18px', textAlign: 'center' }}>
                        <span style={statusBadge(s.status)}>
                          {active
                            ? txt('Actif', 'نشط', 'Active')
                            : txt('Inactif', 'غير نشط', 'Inactive')}
                        </span>
                      </td>
                      {canEdit && (
                        <td style={{ padding: '12px 18px', textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEdit(s);
                            }}
                            style={{
                              background: '#f0fdf4',
                              border: '1px solid #bbf7d0',
                              color: '#047857',
                              borderRadius: '6px',
                              padding: '6px 12px',
                              fontSize: '12px',
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            {txt('Modifier', 'تعديل', 'Edit')}
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!canEdit && (
        <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '12px' }}>
          {txt(
            'L’ajout et la modification nécessitent la permission « gestion des administrateurs ».',
            'الإضافة والتعديل يتطلبان صلاحية إدارة المديرين.',
            'Add and edit require the admin management permission.'
          )}
        </p>
      )}

      {/* Modale ajout / modification */}
      {modalOpen && (
        <>
          <div
            role="presentation"
            onClick={closeModal}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.45)',
              zIndex: 2000,
            }}
          />
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 2001,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '16px',
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                pointerEvents: 'auto',
                background: '#fff',
                borderRadius: '14px',
                width: '100%',
                maxWidth: '720px',
                maxHeight: '90vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  padding: '16px 20px',
                  borderBottom: '1px solid #e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  background: '#fafafa',
                }}
              >
                <div>
                  <div style={{ fontSize: '17px', fontWeight: 700, color: '#1f2937' }}>
                    {modalMode === 'add'
                      ? txt('Ajouter une matière', 'إضافة مادة', 'Add subject')
                      : modalMode === 'edit'
                      ? txt('Modifier la matière', 'تعديل المادة', 'Edit subject')
                      : txt('Détail de la matière', 'تفاصيل المادة', 'Subject details')}
                  </div>
                  {(modalMode === 'edit' || modalMode === 'view') && editingId != null && (
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span>#{editingId}</span>
                      <span style={statusBadge(formStatus)}>
                        {isActiveStatus(formStatus)
                          ? txt('Actif', 'نشط', 'Active')
                          : txt('Inactif', 'غير نشط', 'Inactive')}
                      </span>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={saving}
                  aria-label="Close"
                  style={{
                    border: 'none',
                    background: '#f3f4f6',
                    borderRadius: '8px',
                    width: '32px',
                    height: '32px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    color: '#6b7280',
                  }}
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
                <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
                  {modalError && (
                    <div
                      style={{
                        marginBottom: '14px',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        background: '#fef2f2',
                        color: '#b91c1c',
                        fontSize: '13px',
                      }}
                    >
                      {modalError}
                    </div>
                  )}

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '14px',
                    }}
                  >
                    {(['ar', 'fr', 'en'] as const).map((loc) => (
                      <div
                        key={loc}
                        style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '10px',
                          padding: '12px',
                          background: '#fafafa',
                        }}
                      >
                        <div
                          style={{
                            fontSize: '12px',
                            fontWeight: 700,
                            color: loc === 'ar' ? '#2e7d32' : loc === 'fr' ? '#1565c0' : '#f57f17',
                            marginBottom: '10px',
                          }}
                        >
                          {LANG_LABELS[loc]}
                        </div>
                        <label style={{ display: 'block', fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                          {txt('Nom', 'الاسم', 'Name')}
                          {!readOnly && ' *'}
                        </label>
                        {readOnly ? (
                          <div style={{ ...fieldStyle(loc), minHeight: '36px' }} dir={loc === 'ar' ? 'rtl' : 'ltr'}>
                            {form[loc].name || '—'}
                          </div>
                        ) : (
                          <input
                            required
                            value={form[loc].name}
                            onChange={(e) => setField(loc, 'name', e.target.value)}
                            style={fieldStyle(loc)}
                            dir={loc === 'ar' ? 'rtl' : 'ltr'}
                          />
                        )}
                        <label style={{ display: 'block', fontSize: '11px', color: '#6b7280', marginTop: '8px', marginBottom: '4px' }}>
                          {txt('Description', 'الوصف', 'Description')}
                        </label>
                        {readOnly ? (
                          <div
                            style={{ ...fieldStyle(loc), minHeight: '52px', whiteSpace: 'pre-wrap' }}
                            dir={loc === 'ar' ? 'rtl' : 'ltr'}
                          >
                            {displayDescription(loc)}
                          </div>
                        ) : (
                          <textarea
                            value={form[loc].description}
                            onChange={(e) => setField(loc, 'description', e.target.value)}
                            rows={2}
                            placeholder={txt('(optionnel)', '(اختياري)', '(optional)')}
                            style={{ ...fieldStyle(loc), resize: 'vertical' }}
                            dir={loc === 'ar' ? 'rtl' : 'ltr'}
                          />
                        )}
                      </div>
                    ))}
                  </div>

                  {modalMode === 'edit' && (
                    <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '12px', lineHeight: 1.45 }}>
                      {txt(
                        'Si vous modifiez le nom sans toucher à la description, la description enregistrée reste inchangée.',
                        'عند تعديل الاسم دون تغيير الوصف، يبقى الوصف المخزّن كما هو.',
                        'Changing the name without editing the description keeps the stored description.'
                      )}
                    </p>
                  )}

                  {modalMode === 'edit' && (
                    <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px dashed #e5e7eb' }}>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#9ca3af', marginBottom: '6px' }}>
                        {txt('Statut (rarement modifié)', 'الحالة (نادرًا ما تُغيّر)', 'Status (rarely changed)')}
                      </label>
                      <select
                        value={formStatus}
                        onChange={(e) => setFormStatus(e.target.value)}
                        style={{
                          padding: '7px 12px',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb',
                          fontSize: '13px',
                          minWidth: '140px',
                          background: '#fff',
                          color: '#374151',
                        }}
                      >
                        <option value="نشط">{txt('Actif', 'نشط', 'Active')}</option>
                        <option value="غير نشط">{txt('Inactif', 'غير نشط', 'Inactive')}</option>
                      </select>
                    </div>
                  )}
                </div>

                <div
                  style={{
                    padding: '14px 20px',
                    borderTop: '1px solid #e5e7eb',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '10px',
                    background: '#fafafa',
                  }}
                >
                  <button
                    type="button"
                    onClick={closeModal}
                    disabled={saving}
                    style={{
                      padding: '10px 18px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      background: '#fff',
                      color: '#374151',
                      fontWeight: 600,
                      fontSize: '13px',
                      cursor: saving ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {readOnly ? txt('Fermer', 'إغلاق', 'Close') : txt('Annuler', 'إلغاء', 'Cancel')}
                  </button>
                  {readOnly && canEdit && (
                    <button
                      type="button"
                      onClick={switchToEdit}
                      style={{
                        padding: '10px 22px',
                        borderRadius: '8px',
                        border: '1px solid #bbf7d0',
                        background: '#f0fdf4',
                        color: '#047857',
                        fontWeight: 600,
                        fontSize: '13px',
                        cursor: 'pointer',
                      }}
                    >
                      {txt('Modifier', 'تعديل', 'Edit')}
                    </button>
                  )}
                  {!readOnly && (
                    <button
                      type="submit"
                      disabled={saving}
                      style={{
                        padding: '10px 22px',
                        borderRadius: '8px',
                        border: 'none',
                        background: '#059669',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '13px',
                        cursor: saving ? 'wait' : 'pointer',
                        opacity: saving ? 0.75 : 1,
                      }}
                    >
                      {saving
                        ? '…'
                        : modalMode === 'add'
                        ? txt('Ajouter', 'إضافة', 'Add')
                        : txt('Enregistrer', 'حفظ', 'Save')}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
